import React, {
  useState, Fragment, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

const getProjectColor = (projectName) => {
  const colors = [
    '#6B7280',
    '#EF4444',
    '#8B5CF6',
    '#10B981',
    '#F59E0B',
    '#3B82F6',
    '#EC4899',
    '#14B8A6',
    '#F97316',
    '#84CC16',
  ];

  let hash = 0;
  for (let i = 0; i < projectName.length; i += 1) {
    // eslint-disable-next-line
    hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};
function ProjectCircle({ projectName }) {
  const firstChar = projectName ? projectName.charAt(0).toUpperCase() : '?';
  const backgroundColor = getProjectColor(projectName);

  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        fontWeight: 'bold',
        marginRight: '8px',
        flexShrink: 0,
      }}
    >
      {firstChar}
    </div>
  );
}
ProjectCircle.propTypes = {
  projectName: PropTypes.string.isRequired,
};

function UrlActivity({
  data, hourBlock, additionalProperty, projects = [],
}) {
  const [expanded, setExpanded] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (websiteKey, id) => {
    setExpanded(expanded === websiteKey && id === expandedId ? null : websiteKey);
    setExpandedId(id);
  };

  const getProjectName = (projectId, projects) => {
    if (!projectId || !projects || projects.length === 0) {
      return projectId ? `Project ${projectId}` : 'No Project';
    }

    const project = projects.find((p) => p.id === projectId
      || p.id === parseInt(projectId, 10)
      || p.id === projectId.toString());

    return project ? project.name : `Project ${projectId}`;
  };

  const getCleanUrl = (fullUrl) => {
    try {
      if (!fullUrl || fullUrl === '') return 'N/A';
      const url = new URL(fullUrl);
      return url.origin;
    } catch (error) {
      return fullUrl;
    }
  };

  const getWebsiteName = (domain) => {
    try {
      if (!domain || domain === '') return 'Unknown';
      const cleanDomain = domain.replace('www.', '').replace('.com', '');
      return cleanDomain.charAt(0).toUpperCase() + cleanDomain.slice(1);
    } catch {
      return 'Unknown';
    }
  };

  const formatAccurateTime = (seconds) => {
    if (!seconds || seconds === 0) return '0s';

    const numSeconds = Math.round(seconds);
    const hours = Math.floor(numSeconds / 3600);
    const minutes = Math.floor((numSeconds % 3600) / 60);
    const remainingSeconds = numSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const additionalData = useMemo(() => {
    const browserActivityData = data[additionalProperty];

    if (!browserActivityData || typeof browserActivityData !== 'object') {
      return {};
    }

    const projectGroups = {};

    Object.keys(browserActivityData).forEach((websiteDomain) => {
      const websiteData = browserActivityData[websiteDomain];

      if (!websiteData || typeof websiteData !== 'object') {
        return;
      }

      const websiteName = getWebsiteName(websiteDomain);
      const urls = websiteData.urls || [];

      if (!Array.isArray(urls) || urls.length === 0) {
        return;
      }

      const projectId = urls[0]?.projectId || data?.projectId || null;
      const projectName = getProjectName(projectId, projects);

      if (!projectGroups[projectName]) {
        projectGroups[projectName] = {};
      }

      if (!projectGroups[projectName][websiteName]) {
        projectGroups[projectName][websiteName] = {
          urls: [],
          totalTimeSpent: 0,
          projectId,
        };
      }

      const websiteTotalTime = 0;

      urls.forEach((urlItem) => {
        const projectGroupName = getProjectName(urlItem.projectId || data?.projectId, projects);
        const url = urlItem.url || urlItem.uri || websiteDomain;
        const browserName = urlItem.browserName || 'Unknown Browser';
        const urlTimeSpent = urlItem.secondsElapsed || urlItem.timeSpent || 0;

        if (!projectGroups[projectGroupName]) {
          projectGroups[projectGroupName] = {};
        }

        if (!projectGroups[projectGroupName][websiteName]) {
          projectGroups[projectGroupName][websiteName] = {
            urls: [],
            totalTimeSpent: 0,
            projectId: urlItem.projectId || data?.projectId,
          };
        }

        projectGroups[projectGroupName][websiteName].urls.push({
          url: getCleanUrl(url),
          fullUrl: url,
          timeSpent: formatAccurateTime(urlTimeSpent),
          rawSeconds: urlTimeSpent,
          browserName,
          projectId: urlItem.projectId || data?.projectId,
        });

        projectGroups[projectGroupName][websiteName].totalTimeSpent += urlTimeSpent;
      });

      projectGroups[projectName][websiteName].totalTimeSpent += websiteTotalTime;
    });

    return projectGroups;
  }, [data, additionalProperty, projects]);

  if (!additionalData || Object.keys(additionalData).length === 0) {
    return (
      <Fragment key={`${additionalProperty}_${hourBlock}`}>
        <div className="rounded-xl border shadow mb-3">
          <table className="w-100 text-sm text-left">
            <thead className="border-b">
              <tr>
                <th className="py-2 px-3" colSpan={4}>
                  {moment().set({ hours: parseInt(hourBlock, 10), minutes: 0 })
                    .format('ddd, MMM DD, YYYY h:mm A')}
                  {' '}
                  +06
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-3" colSpan={4}>
                  <em>No URL activity data found for this hour</em>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment key={`${additionalProperty}_${hourBlock}`}>
      <div className="rounded-xl border shadow mb-3">
        <table
          className="w-100 text-sm text-left"
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <thead className="border-b">
            <tr>
              <th className="py-2 px-3" colSpan={4}>
                {moment().set({ hours: parseInt(hourBlock, 10), minutes: 0 }).format('ddd, MMM DD, YYYY h:mm A Z')}
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(additionalData).map((projectName) => (
              <Fragment key={projectName}>
                {Object.keys(additionalData[projectName]).map((websiteName) => {
                  const websiteData = additionalData[projectName][websiteName];
                  const websiteKey = `${projectName}_${websiteName}`;

                  return (
                    <Fragment key={websiteKey}>
                      <tr
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => websiteData.urls
                          && websiteData.urls.length > 1 && toggleExpand(websiteKey, websiteKey)}
                      >
                        <td className="py-2 px-3" style={{ width: '25%' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <ProjectCircle projectName={projectName} size={24} />
                            <div>
                              <strong>{projectName}</strong>
                            </div>
                          </div>
                        </td>

                        <td className="py-2 px-3" style={{ width: '25%' }}>
                          {websiteData.urls && websiteData.urls.length > 1 ? (
                            <span className="mr-2 pointer-event">
                              {expanded === websiteKey && expandedId === websiteKey ? '−' : '+'}
                            </span>
                          ) : (
                            <span className="mr-2">•</span>
                          )}
                          {websiteName}
                        </td>

                        <td
                          className="py-2 px-3"
                          style={{
                            width: '25%',
                            maxWidth: '0',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span
                            className="text-blue-600"
                            title={websiteData.urls[0]?.fullUrl || 'N/A'}
                            style={{
                              display: 'inline-block',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {websiteData.urls[0]?.url || 'N/A'}
                          </span>
                        </td>

                        <td className="py-2 px-3" style={{ width: '25%' }}>
                          <strong>{formatAccurateTime(websiteData.totalTimeSpent)}</strong>
                        </td>
                      </tr>

                      {websiteData.urls && expanded === websiteKey
                      && expandedId === websiteKey && websiteData.urls.length > 1 && (
                        <>
                          {websiteData.urls.map((urlData, idx) => (
                            <tr
                              key={idx}
                              style={{
                                /* eslint-disable-next-line */
                                background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.60) 0%, rgba(255, 255, 255, 0.60) 100%), #D8E6FC',
                              }}
                            >
                              {/* eslint-disable-next-line */}
                              <td
                                className="py-2 px-5"
                                style={{
                                  width: '25%',
                                  borderBottom: '1px solid #A7C5F2',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <small className="text-muted" />
                                </div>
                              </td>
                              <td
                                className="py-2 px-5"
                                style={{
                                  width: '25%',
                                  borderBottom: '1px solid #A7C5F2',
                                }}
                              >
                                {websiteName}
                              </td>
                              <td
                                className="py-2 px-3"
                                style={{
                                  width: '25%',
                                  maxWidth: '0',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  borderBottom: '1px solid #A7C5F2',
                                }}
                              >
                                <span
                                  className="text-blue-600 text-xs"
                                  title={urlData.fullUrl}
                                  style={{
                                    fontSize: '12px',
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {urlData.fullUrl}
                                </span>
                              </td>
                              <td
                                className="py-2 px-3"
                                style={{
                                  width: '25%',
                                  borderBottom: '1px solid #A7C5F2',
                                }}
                              >
                                {urlData.timeSpent}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Fragment>
  );
}

UrlActivity.propTypes = {
  data: PropTypes.shape({
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  hourBlock: PropTypes.string.isRequired,
  additionalProperty: PropTypes.string.isRequired,
  projects: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })),
};

export default UrlActivity;
